<script setup lang="ts">
import {
    computed,
    ref,
} from 'vue';

import { $t } from '@/i18n';
import { hospitalName } from '@/localStorage';
import { getAssessmentStatistics } from '@/repository';

import TruCard from '../TruCard.vue';

const last30DaysCertsActivated = ref(null);
const last30DaysCertsIssued = ref(null);
const last30DaysDollarsPaid = ref(null);
const last30DaysEnrollmentCount = ref(null);
const lifetimeDollarsPaid = ref(null);
const lifetimeEnrollmentCount = ref(null);

const header = computed(() =>
    $t('dashboard.stats.header', { hospitalName: hospitalName.value }));

getAssessmentStatistics()
    .then((res) => {
        last30DaysCertsActivated.value = res[8].Value;
        last30DaysCertsIssued.value = res[2].Value;
        last30DaysDollarsPaid.value = res[5].Value;
        last30DaysEnrollmentCount.value = res[0].Value;
        lifetimeDollarsPaid.value = res[6].Value;
        lifetimeEnrollmentCount.value = res[1].Value;
    });
</script>

<template>
    <div class="dashboard-stats">
        <TruCard :header="header">
            <div class="title flex mb-4">
                <img
                    class="w-10 mr-4"
                    src="../../assets/images/graph-arrow-up.svg"
                    :alt="$t('dashboard.stats.altText')"
                >

                <div class="title desk-body-bold self-end">
                    {{ $t('dashboard.stats.title') }}
                </div>
            </div>

            <hr class="mb-10">

            <div class="dashboard-stats-data flex justify-evenly">
                <div class="flex flex-col">
                    <div class="flex flex-col items-center mb-9">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.newMembers') }}
                        </div>

                        <div class="flex items-center">
                            <span class="number mr-1">
                                {{ last30DaysEnrollmentCount }}
                            </span>
                            <span class="desk-cap2-italic">
                                {{ $t('dashboard.stats.last30Days') }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col items-center">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.totalMembers') }}
                        </div>

                        <div class="number">
                            {{ lifetimeEnrollmentCount }}
                        </div>
                    </div>
                </div>

                <div class="vl" />

                <div class="flex flex-col">
                    <div class="flex flex-col items-center mb-9">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.claimsPaidLast30') }}
                        </div>

                        <div class="number">
                            {{ $n(last30DaysDollarsPaid, 'currency') }}
                        </div>
                    </div>

                    <div class="flex flex-col items-center">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.claimsPaidLifetime') }}
                        </div>

                        <div class="number">
                            {{ $n(lifetimeDollarsPaid, 'currency') }}
                        </div>
                    </div>
                </div>

                <div class="vl" />

                <div class="flex flex-col">
                    <div class="flex flex-col items-center mb-9">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.examDayOffersSent') }}
                        </div>

                        <div class="flex items-center">
                            <span class="number mr-1">
                                {{ last30DaysCertsIssued }}
                            </span>

                            <span class="desk-cap2-italic">
                                {{ $t('dashboard.stats.last30Days') }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col items-center">
                        <div class="desk-cap2-reg mb-1">
                            {{ $t('dashboard.stats.examDayOffersActivated') }}
                        </div>

                        <div class="flex items-center">
                            <span class="number mr-1">
                                {{ last30DaysCertsActivated }}
                            </span>

                            <span class="desk-cap2-italic">
                                {{ $t('dashboard.stats.last30Days') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </TruCard>
    </div>
</template>

<style scoped lang="scss">
.dashboard-stats {
    :deep(.tru-card-body) {
        display: flex;
        flex-direction: column;
        padding: 4rem;
    }

    hr {
        border-top: 2px solid var(--tru-web-body);
    }

    .number {
        color: var(--tru-blue);
        font-family: Mulish;
        font-size: 3.25rem;
        font-weight: 800;
        line-height: 3.75rem;
    }

    .vl {
        border-right: 1px solid var(--tru-charcoal-tint70);
        min-height: 100%;
    }

    .dashboard-stats-data {
        gap: 2rem;

        > .flex {
            flex: 1 1 0;
            min-width: 0;
        }
    }
}
</style>
